import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useMatch } from 'react-router-dom'
import {
	LinearProgress,
	linearProgressClasses,
	Typography,
} from '@mui/material'

import BrideImage from '../atoms/BrideImage/BrideImage'
import Venues from '../atoms/Venues/Venues'
import CategoryTree from '../atoms/CategoryTree/CategoryTree'
import ButtonCustom from '../atoms/button/ButtonCustom'
import Response from '../atoms/Response/Response'
import Progress from './Progress/Progress'
import PackagesListView from '../profileCards/vendor/PackagesListView'
import { closeModal, openModal, setModalData } from '../../features/commonSlice'

import {
	getKazooMembers,
	getKazooDashboard,
	getCuratedImages,
	fetchCategoryBudget,
	getKazooDate,
	fetchKazooDetailsNew,
} from '../../features/kazooEventSlice'
import { getEvents } from '../../features/schedulerSlice'
import { formatCommonAmount } from '../../util/formatAmount'

import styles from './VendorWeddingInfo.module.scss'
import StatusBtn from '../atoms/status/StatusBtn'
import { TAX_PERCENTAGE } from '../../util/constants'
import { ProposalPopup } from '../VendorTracker/modal/ProposalPopup'
import { ProposalHistory } from '../VendorTracker/modal/ProposalHistory'
import { getProposalHistory } from '../../features/proposalSlice'

const VendorWeddingInfo = ({
	kazooId,
	proposalData,
	editBid,
	categoryBudget = 0,
}) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const isVendorWeddingInfoPage = useMatch('/wedding/details/*') !== null
	const status = proposalData?.status || ''
	const authUser = useSelector((s) => s.auth.user)
	const kazooEvent = useSelector((s) => s.kazooEvent) || {}
	const date = useSelector((state) => state.kazooEvent.kazooDate)
	const tax = proposalData?.taxPercentage || TAX_PERCENTAGE || 10
	const [proposalModalOpen, setProposalModalOpen] = useState(false)
	const members = useSelector((s) => s.kazooEvent.kazooMembers) || {}
	const overallBudget = useSelector((state) => state.kazooEvent.budget)
	const dashboard = useSelector((s) => s.kazooEvent.dashboardData) || {}
	const timelineEvents = useSelector((s) => s.scheduler.timelineEvents) || []
	const proposalHistory = useSelector((s) => s.proposal.proposalHistory) || []
	const selectedImages = useSelector(
		(state) => state.kazooEvent.selectedImages
	)
	const backendCategoryBudget = useSelector(
		(s) => s.kazooEvent.categoryBudget
	)

	const isProposalContext = Boolean(proposalData?.proposalId || proposalData)

	useEffect(() => {
		if (!kazooId || isProposalContext) return
		dispatch(getKazooMembers(kazooId))
		dispatch(getKazooDashboard(kazooId))
		dispatch(getEvents(kazooId))
		dispatch(getCuratedImages(kazooId))
		dispatch(getKazooDate(kazooId))
		dispatch(fetchKazooDetailsNew(kazooId))
	}, [dispatch, kazooId, isProposalContext])

	const weddingBrideName =
		members.bride?.preferredName ||
		(members.bride?.firstName && members.bride?.lastName
			? `${members.bride.firstName} ${members.bride.lastName}`
			: null)

	const weddingGroomName =
		members.groom?.preferredName ||
		(members.groom?.firstName && members.groom?.lastName
			? `${members.groom.firstName} ${members.groom.lastName}`
			: null)

	const brideName = isProposalContext
		? proposalData?.brideName
		: weddingBrideName

	const groomName = isProposalContext
		? proposalData?.groomName
		: weddingGroomName

	const coupleTitle = isProposalContext
		? brideName && groomName
			? `${brideName} & ${groomName}`
			: proposalData?.kazooName || 'Wedding'
		: brideName && groomName
			? `${brideName} & ${groomName}`
			: dashboard.kazooName || 'Wedding'

	const bridePic = isProposalContext
		? proposalData?.brideProfilePic || '/images/Avatar.svg'
		: members.bride?.profilePic || '/images/Avatar.svg'

	const groomPic = isProposalContext
		? proposalData?.groomProfilePic || '/images/Avatar.svg'
		: members.groom?.profilePic || '/images/Avatar.svg'

	const rawWeddingDate = isProposalContext
		? proposalData?.date
		: date || dashboard.startDate
	const weddingDate = rawWeddingDate
		? new Date(rawWeddingDate).toLocaleDateString(undefined, {
				year: 'numeric',
				month: 'short',
				day: '2-digit',
			})
		: 'TBD'

	const visionBoard =
		isProposalContext || !selectedImages?.length
			? ['/images/CuratedList.jpg']
			: selectedImages

	const ceremonyAddr =
		kazooEvent.ceremonyVenue?.address || 'Ceremony venue TBD'
	const receptionAddr =
		kazooEvent.receptionVenue?.address || 'Reception venue TBD'

	const expectedGuestCount = isProposalContext
		? proposalData?.guests ?? null
		: dashboard.guestCount ?? null
	const expectedGuestCountDisplay = expectedGuestCount ?? 'Not set'

	const displayOverallBudget = isProposalContext
		? proposalData?.overallBudget ?? 0
		: overallBudget

	const vendorCategory =
		authUser?.businessCategory ||
		authUser?.serviceType ||
		authUser?.businessType ||
		authUser?.businessTypes?.join(',') ||
		authUser?.type ||
		'Miscellaneous'

	useEffect(() => {
		if (!kazooId || !vendorCategory || isProposalContext) return
		dispatch(fetchCategoryBudget({ category: vendorCategory, kazooId }))
	}, [dispatch, kazooId, vendorCategory, isProposalContext])

	useEffect(() => {
		if (!proposalData?.proposalId) return
		dispatch(getProposalHistory(proposalData?.proposalId))
	}, [dispatch, proposalData])

	const { vendorCategoryPaid, vendorCategoryTotal, vendorCategoryItems } =
		useMemo(() => {
			const result = {
				vendorCategoryPaid: 0,
				vendorCategoryTotal: 0,
				vendorCategoryItems: [],
			}
			const alloc = kazooEvent.categoryAllocations
			if (!alloc) return result

			const arrays = Array.isArray(alloc)
				? alloc
				: Array.from(
						alloc instanceof Map
							? alloc.values()
							: Object.values(alloc)
					)

			for (const arr of arrays) {
				if (!Array.isArray(arr)) continue
				for (const line of arr) {
					const vId = line?.vendor?.vendorId
					if (!vId) continue
					if (String(vId) !== String(authUser?._id)) continue

					const paid = Number(line?.paid || 0)
					const total = Number(line?.total || 0)

					result.vendorCategoryPaid += paid
					result.vendorCategoryTotal += total
					result.vendorCategoryItems.push({
						title: line.item || 'Service Item',
						sub: [
							total
								? `Budget: $${total.toLocaleString()}`
								: 'Budget: N/A',
							paid
								? `Paid: $${paid.toLocaleString()}`
								: 'Paid: $0',
						],
					})
				}
			}
			return result
		}, [kazooEvent.categoryAllocations, authUser?._id])

	const effectiveCategoryTotal = categoryBudget
		? categoryBudget
		: isProposalContext
			? 0
			: typeof backendCategoryBudget === 'number' &&
				  !Number.isNaN(backendCategoryBudget)
				? backendCategoryBudget
				: vendorCategoryTotal

	const categoryPct =
		effectiveCategoryTotal && displayOverallBudget
			? (effectiveCategoryTotal / displayOverallBudget) * 100
			: 0

	const categoryBudgetLine =
		effectiveCategoryTotal > 0 && displayOverallBudget
			? `$${effectiveCategoryTotal.toLocaleString()} / $${displayOverallBudget.toLocaleString()}`
			: '—'

	const dayTimeline = useMemo(() => {
		if (isProposalContext) return []
		const items = (timelineEvents || [])
			.filter((event) => {
				const eventDate = new Date(event.StartTime || event.startTime)
				const weddingDate = new Date(date)
				return (
					eventDate.getFullYear() === weddingDate.getFullYear() &&
					eventDate.getMonth() === weddingDate.getMonth() &&
					eventDate.getDate() === weddingDate.getDate()
				)
			})
			.slice()
			.sort(
				(a, b) =>
					new Date(a.StartTime || a.startTime) -
					new Date(b.StartTime || b.startTime)
			)
		return items.slice(0, 4).map((ev) => ({
			time: new Date(ev.StartTime || ev.startTime).toLocaleTimeString(
				undefined,
				{
					hour: '2-digit',
					minute: '2-digit',
				}
			),
			text: ev.Subject || ev.title || 'Event',
			desc: ev.Description || ev.description || '',
		}))
	}, [timelineEvents, date, isProposalContext])

	const captureCount = useSelector((s) => s.kazooEvent.captureCount) || {}

	const captureCategoryItems = useMemo(() => {
		if (isProposalContext) return []
		const items = []

		//THIS LOGIC NEEDS TO BE CHANGED. NOT ALL VENDORS OFFER THE SAME SERVICE
		const photography = Number(captureCount.photography || 0)
		const videography = Number(captureCount.videography || 0)
		const contentCreation = Number(captureCount.contentCreation || 0)
		const photobooth = Number(captureCount.photobooth || 0)

		// Photography + Cinematography
		if (photography || videography) {
			const sub = []
			if (photography) {
				sub.push(
					`${photography} Photographer${photography === 1 ? '' : 's'}`
				)
			}
			if (videography) {
				sub.push(
					`${videography} Cinematographer${videography === 1 ? '' : 's'}`
				)
			}
			items.push({
				title: 'Photography',
				subTitle: sub,
			})
		}

		// Content creators
		if (contentCreation) {
			items.push({
				title: 'Content Creation',
				subTitle: [
					`${contentCreation} Content Creator${contentCreation === 1 ? '' : 's'}`,
				],
			})
		}

		// Photobooths
		if (photobooth) {
			items.push({
				title: 'Photobooth',
				subTitle: [
					`${photobooth} Photobooth${photobooth === 1 ? '' : 's'}`,
				],
			})
		}

		return items
	}, [captureCount, isProposalContext])

	const taxAmount = useMemo(() => {
		if (proposalData?.total) return proposalData?.total * (tax / 100)
		return 0
	}, [proposalData])
	const setWithdrawBid = () => dispatch(openModal('withdrawProposal'))
	const handleModalToggle = () => setProposalModalOpen((prev) => !prev)

	return (
		<>
			<div className={styles.VendorInfoScreen}>
				<div className={styles.VendorInfoCard}>
					{/* Header */}
					<div className={styles.VendorInfoCardHead}>
						<BrideImage srcOne={bridePic} srcTwo={groomPic} />
						<div className={styles.VendorInfoCardHeadText}>
							<div className={styles.VendorInfoCardHeadTextTitle}>
								<h1>{coupleTitle}</h1>
								<div
									className={
										styles.VendorInfoCardHeadTextTitleSub
									}
								>
									<p>
										<img
											src="/images/calendar.svg"
											alt="Calendar"
										/>{' '}
										<span>{weddingDate}</span>
									</p>
									<p>
										<img
											src="/images/TotalGuests.svg"
											alt="Guests"
										/>{' '}
										<span>
											{expectedGuestCountDisplay}
											{expectedGuestCount == null ? '' : ' Guests'}
										</span>
									</p>
								</div>
							</div>
							<div
								className={
									styles.VendorInfoCardHeadTextResponse
								}
							>
								{/* <Response label="Coming soon" /> */}
								<ButtonCustom
									variant="outlined"
									size="lg"
									onClick={() =>
										isVendorWeddingInfoPage
											? navigate(
													`/wedding/vendor-chat/${kazooId}`
												)
											: navigate('/chat')
									}
								>
									<img
										src="/images/chat-icon.svg"
										alt="Chat"
									/>
									Chat Now
								</ButtonCustom>
							</div>
						</div>
					</div>

					{/* Body */}
					<div className={styles.VendorInfoCardBody}>
						<div className={styles.VendorInfoGride}>
							{/* Vision Board + Venues */}
							<div className={styles.VendorInfoGrideItem}>
								{/* Vision Board */}
								<div className={styles.ProposalVisionBoard}>
									<div
										className={
											styles.ProposalGrideItemTitleMain
										}
									>
										<h4
											className={
												styles.ProposalGrideItemTitle
											}
										>
											Vision Board
										</h4>
									</div>
									<div
										className={styles.ProposalGrideItemBody}
									>
										<div
											className={
												styles.ProposalVisionBoardItem
											}
										>
											{visionBoard
												.slice(0, 2)
												.map((src, i) => (
													<figure key={i}>
														<img
															src={
																typeof src ===
																'string'
																	? src
																	: String(
																			src
																		)
															}
															alt={`vision-${i}`}
														/>
													</figure>
												))}
											{visionBoard.length > 2 && (
												<figure
													className={
														styles.ProposalVisionBoardItemMore
													}
												>
													<img
														src={visionBoard[2]}
														alt="more"
													/>
													<div
														className={
															styles.ProposalVisionBoardItemMoreText
														}
													>
														<span>
															<span
																className={
																	styles.TextCount
																}
															>
																+
																{visionBoard.length -
																	2}
															</span>
															View all
														</span>
													</div>
												</figure>
											)}
										</div>
									</div>
								</div>

								<hr />

								{/* Venues */}
								{/* TODO: uncomment when implemented */}
								{/* <div className={styles.ProposalVenues}><div className={styles.ProposalGrideItemTitleMain}>
								<h4 className={styles.ProposalGrideItemTitle}>Venues</h4></div>
								<div className={styles.ProposalGrideItemBody}><div className={styles.ProposalVenuesItem}>
								<Venues location={ceremonyAddr} /><Venues location={receptionAddr} /></div></div></div> */}
							</div>

							{/* Budget + Category Details */}
							<div className={styles.VendorInfoGrideItem}>
								{/* Budget */}
								<div className={styles.ProposalBudget}>
									<div
										className={
											styles.ProposalGrideItemTitleMain
										}
									>
										<h4
											className={
												styles.ProposalGrideItemTitle
											}
										>
											Budget
										</h4>
									</div>
									<div
										className={styles.ProposalGrideItemBody}
									>
										<div
											className={
												styles.ProposalBudgetItem
											}
										>
											<div
												className={
													styles.ProposalBudgetItemText
												}
											>
												<p>Category Budget</p>
												<p
													className={
														styles.ProposalBudgetItemTextValue
													}
												>
													{categoryBudgetLine}
												</p>
											</div>
											<div>
												<LinearProgress
													variant="determinate"
													value={categoryPct}
													sx={{
														padding: '4px',
														borderRadius: '12px',
														backgroundColor:
															'#FAF4F2',
														[`& .${linearProgressClasses.bar}`]:
															{
																backgroundColor:
																	'#7CA6BD',
																borderRadius:
																	'50px',
															},
													}}
												/>
											</div>
											<div
												className={
													styles.ProposalOverallBudget
												}
											>
												<p>
													Overall Budget:{' '}
													<span>
														{displayOverallBudget}
													</span>
												</p>
											</div>
										</div>
									</div>
								</div>

								<hr />

								{/* Category Details */}
								<div className={styles.ProposalCategoryDetails}>
									<div
										className={
											styles.ProposalGrideItemTitleMain
										}
									>
										<h4
											className={
												styles.ProposalGrideItemTitle
											}
										>
											Category Details
										</h4>
									</div>
									<div
										className={styles.ProposalGrideItemBody}
										style={{
											maxHeight: '250px',
											overflow: 'auto',
										}}
									>
										<div
											className={
												styles.ProposalCategoryDetailsItem
											}
										>
											{captureCategoryItems.length ? (
												captureCategoryItems.map(
													(it, idx) => (
														<CategoryTree
															key={idx}
															title={it.title}
															subTitle={
																it.subTitle
															}
														/>
													)
												)
											) : (
												<Typography
													variant="body2"
													color="text.secondary"
												>
													Capture preferences will
													appear here once they're
													set.
												</Typography>
											)}
										</div>
									</div>
								</div>
								{proposalData?.packageDetail?.name && (
									<>
										<hr />
										<div>
											<h4
												className={
													styles.ProposalGrideItemTitle
												}
											>
												Package Details
											</h4>
											<div
												className={
													styles.ProposalGrideItemBody
												}
											>
												<PackagesListView
													cardStyle={{
														boxShadow: 'none',
														border: 'none',
														padding: 0,
													}}
													packages={[
														proposalData?.packageDetail,
													]}
													showActions={false}
												/>
											</div>
										</div>
									</>
								)}
							</div>

							{/* Timeline + Totals */}
							<div className={styles.VendorInfoGrideItem}>
								{/* Day Timeline */}
								<div className={styles.ProposalDayTimeLine}>
									<div
										className={
											styles.ProposalGrideItemTitleMain
										}
									>
										<h4
											className={
												styles.ProposalGrideItemTitle
											}
										>
											Wedding Day Time Line
										</h4>
										<span
											className={
												styles.ProposalDayTimeLineUpdated
											}
										>
											{timelineEvents?.length
												? 'Updated'
												: 'No items yet'}
										</span>
									</div>
									<div
										className={styles.ProposalGrideItemBody}
									>
										<div
											className={
												styles.ProposalDayTimeLineItem
											}
										>
											{dayTimeline.length ? (
												dayTimeline.map((t, i) => (
													<div
														key={i}
														className={
															styles.ProposalDayTimeLineItemTime
														}
													>
														<p
															className={
																styles.TimeText
															}
														>
															{t.time}
														</p>
														<div
															className={
																styles.TimeLineItemText
															}
														>
															<p>{t.text}</p>
															{t.desc ? (
																<span>
																	{t.desc}
																</span>
															) : null}
														</div>
													</div>
												))
											) : (
												<Typography
													variant="body2"
													color="text.secondary"
												>
													Timeline will appear here
													once scheduled.
												</Typography>
											)}
										</div>
									</div>
								</div>

								<hr />

								{/* Totals */}
								<div className={styles.ProposalTotalGuests}>
									<div
										className={styles.ProposalGrideItemBody}
									>
										<div
											className={
												styles.ProposalTotalGuestsItem
											}
										>
											<div
												className={
													styles.ProposalTotalGuestsItemText
												}
											>
												<p>
													<img
														src="/images/TotalGuests.svg"
														alt="Guests"
													/>{' '}
													<span>Expected Guests</span>
												</p>
												<p className={styles.PriceText}>
													{expectedGuestCountDisplay}
												</p>
											</div>
											<div
												className={
													styles.ProposalTotalGuestsItemText
												}
											>
												<p>
													<img
														src="/images/TotalGuests.svg"
														alt="Budget"
													/>{' '}
													<span>Category Budget</span>
												</p>
												<p className={styles.PriceText}>
													{effectiveCategoryTotal > 0
														? `$${effectiveCategoryTotal.toLocaleString()}`
														: 'ΓÇö'}
												</p>
											</div>
											<div
												className={
													styles.ProposalTotalGuestsItemText
												}
											>
												<p>
													<img
														src="/images/TotalGuests.svg"
														alt="Response"
													/>{' '}
													<span>Response Rating</span>
												</p>
												<p className={styles.PriceText}>
													Coming soon
												</p>
											</div>
										</div>
									</div>
								</div>
								<hr />
								{proposalData?.serviceRequirements && (
									<div className={styles.requirements}>
										<p className={styles.heading}>
											Service Requirements
										</p>
										<p className={styles.value}>
											{proposalData?.serviceRequirements ||
												'--'}
										</p>
									</div>
								)}

								{(proposalData?.packageDetail &&
									!!proposalData?.total) ||
								!!proposalData?.bidAmount ||
								!!proposalData?.userBudget ? (
									<div className={styles.totalPriceWrp}>
										<Typography
											variant="body2"
											className={styles.totalPriceWrpText}
										>
											Price Estimate
										</Typography>
										<Typography
											variant="body2"
											className={
												styles.totalPriceWrpValue
											}
										>
											Total:{' '}
											{formatCommonAmount(
												proposalData?.bidAmount ||
													proposalData?.grandTotal ||
													proposalData?.userBudget
											)}
										</Typography>
									</div>
								) : null}
							</div>
						</div>
					</div>

					{/* TODO: uncomment when implemented */}
					{/* <Progress step={4} /> */}
				</div>
			</div>
			{status !== 'New' && (
				<div className={styles.SubmittedBidScreen}>
					<div className={styles.SubmittedBidCard}>
						<div className={styles.SubmittedBidCardHead}>
							<h1>Submitted Bid</h1>
							<div className={styles.SubmittedBidCardHeadRight}>
								<div>
									{status === 'Approved' && (
										<StatusBtn
											label="Bid Approved"
											variant="success"
										/>
									)}
									{status === 'Submitted' && (
										<StatusBtn
											label="Bid Submitted"
											variant="pending"
										/>
									)}
									{['Rejected', 'Declined']?.includes(
										status
									) && (
										<StatusBtn
											label={`Bid ${status}`}
											variant="error"
										/>
									)}
									{status === 'Revision' && (
										<StatusBtn
											label="Revision Requested"
											variant="revision"
										/>
									)}
								</div>
								{['Submitted', 'Revision']?.includes(
									status
								) && (
									<ButtonCustom
										variant="outlined"
										size="sm"
										onClick={editBid}
									>
										<img
											src="/images/edit-2.svg"
											alt="Edit"
										/>
										Edit Bid
									</ButtonCustom>
								)}
								{status === 'Submitted' && (
									<ButtonCustom
										variant="outlined"
										size="sm"
										color="error"
										style={{ color: '#F44336' }}
										onClick={setWithdrawBid}
									>
										<img
											src="/images/close-circle.svg"
											alt="Withdraw"
										/>
										Withdraw Bid
									</ButtonCustom>
								)}
								{proposalHistory?.proposals?.length > 0 && (
									<ButtonCustom
										size="sm"
										variant="outlined"
										onClick={handleModalToggle}
									>
										<img
											alt="Histrory"
											src="/images/clock.svg"
										/>
										Bid Histrory
									</ButtonCustom>
								)}
							</div>
						</div>

						{['Rejected', 'Declined']?.includes(status) &&
							proposalData?.reason && (
								<div
									className={`${styles.ReasonBox} ${styles.rejected}`}
								>
									<p className={styles.ReasonTitle}>
										{status === 'Declined'
											? 'Decline'
											: 'Reject'}{' '}
										Reason
									</p>
									<p className={styles.ReasonText}>
										{proposalData?.reason}
									</p>
								</div>
							)}

						{status === 'Revision' &&
							proposalData?.revision_description && (
								<div
									className={`${styles.ReasonBox} ${styles.revision}`}
								>
									<p className={styles.ReasonTitle}>
										Request Revision
									</p>
									<p className={styles.ReasonText}>
										{proposalData?.revision_description ??
											proposalData?.reason ??
											''}
									</p>
								</div>
							)}

						{proposalData?.packages?.filter(
							(p) => !!p.id || !!p._id
						)?.length > 0 && (
							<div className={styles.BidTableContainer}>
								<div className={styles.BidTableHeader}>
									<span>Package Name</span>
									<span>Quantity</span>
									<span>Unit Price</span>
									<span>Amount</span>
								</div>
								<div className={styles.BidTableBody}>
									{(proposalData?.packages || [])?.map(
										(item, idx) => (
											<React.Fragment key={idx}>
												<div
													className={
														styles.BidTableRow
													}
												>
													<div
														className={
															styles.ColumnContent
														}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{item.name}
														</span>
													</div>
													<div
														className={
															styles.ColumnContent
														}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{item.quantity}
														</span>
													</div>
													<div
														className={
															styles.ColumnContent
														}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{formatCommonAmount(
																item.unitPrice
															)}
														</span>
													</div>
													<div
														className={
															styles.ColumnContent
														}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{formatCommonAmount(
																item.unitPrice *
																	item.quantity
															)}
														</span>
													</div>
												</div>
												{(item.addOns || []).map(
													(addOn, aIdx) => (
														<div
															key={aIdx}
															className={`${styles.BidTableRow} ${styles.addOnRow}`}
														>
															<div
																className={`${styles.ColumnContent} ${
																	aIdx ===
																	item.addOns
																		.length -
																		1
																		? styles.lastAddOn
																		: ''
																}`}
															>
																<span
																	className={
																		styles.SingleValue
																	}
																>
																	{addOn.name}
																</span>
															</div>
															<div
																className={
																	styles.ColumnContent
																}
															>
																<span
																	className={
																		styles.SingleValue
																	}
																>
																	{
																		addOn.quantity
																	}
																</span>
															</div>
															<div
																className={
																	styles.ColumnContent
																}
															>
																<span
																	className={
																		styles.SingleValue
																	}
																>
																	{formatCommonAmount(
																		addOn.unitPrice
																	)}
																</span>
															</div>
															<div
																className={
																	styles.ColumnContent
																}
															>
																<span
																	className={
																		styles.SingleValue
																	}
																>
																	{formatCommonAmount(
																		addOn.unitPrice *
																			addOn.quantity
																	)}
																</span>
															</div>
														</div>
													)
												)}
											</React.Fragment>
										)
									)}
								</div>

								<div className={styles.BidTableFooter}>
									<div className={styles.FooterItem}>
										<span>Subtotal:</span>
										<span>
											{formatCommonAmount(
												proposalData?.total
											)}
										</span>
									</div>
									<div className={styles.FooterItem}>
										<span>Tax({tax}%):</span>
										<span>
											{formatCommonAmount(taxAmount)}
										</span>
									</div>
									<div
										className={`${styles.FooterItem} ${styles.total}`}
									>
										<span>Total Bid Amount:</span>
										<span>
											{formatCommonAmount(
												proposalData?.total + taxAmount
											)}
										</span>
									</div>
								</div>
							</div>
						)}

						<div className={styles.SectionBox}>
							<span className={styles.SectionLabel}>Notes</span>
							<span className={styles.SectionContent}>
								{proposalData?.note || 'No notes provided.'}
							</span>
						</div>

						{proposalData?.attachment && (
							<div className={styles.SectionBox}>
								<span className={styles.SectionLabel}>
									Attachment
								</span>
								<div className={styles.SectionContent}>
									<div className={styles.AttachmentCard}>
										<img
											src="/images/document.svg"
											alt="doc"
										/>
										<div className={styles.AttachmentInfo}>
											<span>
												{proposalData?.attachmentName ||
													'Package Details.pdf'}
											</span>
											<img
												src="/images/task-detail-download.svg"
												alt="download"
												className={styles.options}
												onClick={() => {
													window.open(
														proposalData?.attachment,
														'_blank'
													)
												}}
												style={{ cursor: 'pointer' }}
											/>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			<ProposalPopup kazooData={{ id: proposalData?.proposalId }} />
			<ProposalHistory
				proposalHistory={{ ...proposalHistory, coupleTitle }}
				isOpen={proposalModalOpen}
				handleClose={handleModalToggle}
			/>
		</>
	)
}

export default VendorWeddingInfo
